import type { ReactNode } from "react";
import { cn } from "../cn";
import { outlineChip } from "./chips-variants";

export interface Chip<T extends string> {
  value: T;
  /** Text, or an icon beside text. A node-only label needs `title` for its accessible name. */
  label: ReactNode;
  /**
   * How many rows this filter would leave — rendered in mono beside the
   * label, and part of the button's accessible name, so "Live 8" is
   * what a screen reader says and what a test can match.
   *
   * A string rather than a number because the caller has already decided how
   * to write it. The component never hides an option; a caller may pass only
   * the options it wants shown.
   */
  count?: string;
  /** Shown on hover and to a screen reader when the label alone is terse. */
  title?: string;
  /**
   * Offered but not available yet — a lane with nothing in it, a filter for a
   * thing this account has none of. Shown rather than dropped: an absence
   * costs more trust than a dead end (decisions/0012), and a reader who
   * cannot see the option concludes the product cannot do it. Whoever sets
   * this owes the reader a sentence nearby saying why.
   */
  disabled?: boolean;
}

export interface ChipsProps<T extends string> {
  /** Names the set for a screen reader — "Filter events by what they caused". */
  label: string;
  options: Chip<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  /**
   * `pills` (the default) is the filter row this started as: separate,
   * free-floating, wrapping, and the chosen one is tinted.
   *
   * `segmented` is one control rather than several — an inset track with the
   * chosen option raised out of it. Use it where the options are *modes of the
   * screen* rather than filters on a list: exactly one is always on, choosing
   * another replaces what is below rather than narrowing it, and the set is
   * short enough not to wrap. Tinting the choice is wrong there, because the
   * tint would be the loudest thing on a screen whose subject is underneath
   * it; the raise says the same thing quietly.
   *
   * `outline` is a row of kinds above a library: idle ones are outlined, the
   * chosen one is filled with ink. A tint would read as a status, and here the
   * chosen chip is a place you are standing in.
   */
  variant?: "pills" | "segmented" | "outline";
}

/**
 * A row of filters, one of which is on.
 *
 * Every screen that needed this built its own, because the system had a badge —
 * the right shape — and nothing you could press. Two rounded-full spellings
 * with slightly different padding is how a console starts looking assembled
 * rather than designed.
 *
 * Buttons with `aria-pressed` rather than a radio group: these are commands
 * that change what the server returns, not a value being collected for a form,
 * and a radiogroup would promise arrow-key semantics that a filter row does not
 * have. That holds for `segmented` too, which looks more like a radio group
 * than a filter row does — it is a deliberate hold rather than an oversight,
 * because the semantics did not change and neither did the keyboard, so
 * announcing otherwise would be the promise this paragraph refuses to make.
 *
 * One rule for whoever wires it up: **filter on the server.** A page is chosen
 * server-side, so a chip applied in the browser returns a short page rather than
 * a page of the right rows — which reads as "there were only two" when there
 * were two hundred.
 */
export function Chips<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
  variant = "pills",
}: ChipsProps<T>) {
  const segmented = variant === "segmented";
  const outline = variant === "outline";
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        segmented
          ? "inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[var(--radius-md)] border border-rule bg-surface p-[3px] whitespace-nowrap"
          : outline
            ? "flex flex-wrap gap-1.5"
            : "flex flex-wrap gap-1",
        className,
      )}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            aria-disabled={option.disabled ? true : undefined}
            title={option.title}
            onClick={() => {
              if (option.disabled) return;
              onChange(option.value);
            }}
            className={cn(
              "inline-flex items-center gap-1.5 font-sans font-medium transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              segmented
                ? cn(
                    "shrink-0 rounded-md px-3 py-1.5 text-ui",
                    on ? "bg-surface-2 text-ink" : "text-ink-2 hover:text-ink",
                  )
                : outline
                  ? outlineChip(on)
                  : cn(
                      "rounded-full px-3 py-1 text-meta",
                      on
                        ? "bg-accent-soft text-accent-text"
                        : "bg-surface-2 text-ink-2 hover:text-ink",
                    ),
              // Still focusable, still announced — it is a real option that
              // is not available yet, not a decoration.
              option.disabled && "cursor-not-allowed opacity-50 hover:text-ink-2",
            )}
          >
            {option.label}
            {option.count === undefined ? null : (
              // Inside the button, so it belongs to the accessible name rather
              // than floating beside it as a number with nothing to attach to.
              // Not the design's accent. As a *fill* it measures 2.25:1 on the
              // raised chip — `--color-accent-text` is 4.58 there and would read,
              // but the raised surface already says which chip is on, so a count
              // does not need a colour to carry that as well.
              <span className={cn("font-mono text-micro", on ? "text-ink" : "text-ink-2")}>
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
